import React, { ReactNode, useRef } from 'react'
import cn from 'classnames'

import { getIntentClass, safeInvoke } from '../../../../library'
import { getInputClasses } from './input-helpers'
import { InputUpdate, InputProps, InputShape, InputSize, InputAppearance } from './input-types'
import { BaseProps, IntentProps } from '../../../../types'
import { Close } from '../../../close'

export const inputClassNames = {
  MAIN: 'input',
  INPUT: 'input__text',
  ADDON: 'input__addon',
}

export interface TextInputProps<T = string>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'value'>,
  InputProps,
  BaseProps,
  IntentProps {
  /** Content displayed to the Left of the input */
  after?: ReactNode

  /** Content displayed to the right of the input */
  before?: ReactNode

  /** Inputs don't have children */
  children?: never

  /** Unique identifier for the input */
  field: string

  /** Disables the input from being modified */
  isDisabled?: boolean

  /** Adds a required badge. Does NOT add any validation */
  isRequired?: boolean

  /** Adds a button to easily clear the input */
  isClearable?: boolean

  /** Called when the input is cleared via the 'clear' button */
  onClear?: () => void

  /** Change event handler. Calls function with 'value', 'field', and 'event' arguments */
  onUpdate?: InputUpdate<T>

  /** Sets the value of the input */
  value?: T
}

/**
 * Text Input
 */
export const Input = React.forwardRef<
  HTMLDivElement,
  TextInputProps<React.ReactText>
>((props, ref) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const inputRef = useRef<HTMLInputElement>()

  const {
    after,
    appearance,
    className,
    before,
    field,
    intent,
    isClearable,
    isDisabled,
    isRequired,
    required,
    name,
    onBlur,
    onChange,
    onClear,
    onUpdate,
    onFocus,
    shape,
    size,
    value,
    ...htmlProps
  } = props

  //
  // Handlers
  //

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    safeInvoke(props.onUpdate, value, props.field, e)
    safeInvoke(props.onChange, e)
  }

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsFocused(false)
    safeInvoke(props.onBlur, e)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    safeInvoke(props.onFocus, e)
  }

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (e.defaultPrevented) return
    inputRef.current.focus()
  }

  const handleClear = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    if (props.isDisabled) return
    if (props.value !== undefined) {
      // controlled, send update
      safeInvoke(props.onUpdate, '', props.field)
    } else {
      // uncontrolled, manually clear input
      inputRef.current.value = ''
    }
    safeInvoke(props.onClear)
  }

  //
  // Render
  //

  const renderAttachment = (content: React.ReactNode) => {
    return <div className={inputClassNames.ADDON}> {content} </div>
  }

  const renderClearButton = () => {
    const isControlled = props.value !== undefined
    if (
      !props.isClearable ||
      props.isDisabled ||
      (isControlled && !props.value)
    )
      return null
    return (
      <div className={inputClassNames.ADDON}>
        <Close size={props.size} shape={props.shape} onClick={handleClear} />
      </div>
    )
  }

  const classNames = cn(
    'input',
    {
      '-disabled': isDisabled,
      '-focus': isFocused,
      '-required': isRequired || required,
      '-hidden': props.type === 'hidden',
    },
    getInputClasses({ size, shape, appearance }),
    getIntentClass(props.intent),
    props.className,
  )

  return (
    <div className={classNames} ref={ref} onClick={handleClick}>
      {!!before ? renderAttachment(before) : null}
      <input
        ref={inputRef}
        className={inputClassNames.INPUT}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={isDisabled}
        name={props.field}
        value={value !== null ? value : ''}
        {...htmlProps}
      />
      {!!after ? renderAttachment(after) : null}
      {renderClearButton()}
    </div>
  )
})

Input.displayName = 'Text Input'

Input.defaultProps = {
  shape: InputShape.ROUNDED,
  size: InputSize.MEDIUM,
  appearance: InputAppearance.OUTLINE,
}
